import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Trash2, Package } from 'lucide-react';

export const LineItemRow = ({ item, index, onChange, onRemove }) => {

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // ✅ SAFE FALLBACKS
  const itemName =
    item.custom_name ||
    item.item_snapshot?.item_name ||
    item.item_snapshot?.name ||
    'Unknown Item';

  const itemDescription =
    item.custom_description ||
    item.item_snapshot?.description ||
    '';

  return (
    <Card className="bg-slate-50 border-slate-200" data-testid={`line-item-${index}`}>
      <CardContent className="p-4">

        {/* HEADER */}
        <div className="flex items-start justify-between mb-3">

          <div className="flex items-start gap-3 flex-1 min-w-0">

            <div className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Package className="w-4 h-4 text-emerald-600" />
            </div>

            <div className="flex-1 min-w-0 space-y-2">

              {/* ✅ EDITABLE NAME */}
              <Input
                value={itemName}
                onChange={(e) => onChange(index, 'custom_name', e.target.value)}
                className="h-9 text-sm font-medium"
                placeholder="Item name"
              />

              {/* ✅ EDITABLE DESCRIPTION */}
              <Input
                value={itemDescription}
                onChange={(e) => onChange(index, 'custom_description', e.target.value)}
                className="h-9 text-sm"
                placeholder="Description"
              />

              {/* META INFO (UNCHANGED) */}
              <div className="flex flex-wrap gap-2">
                {item.item_snapshot?.code && (
                  <span className="text-xs text-slate-500">
                    Code: {item.item_snapshot.code}
                  </span>
                )}
                {item.item_snapshot?.UOM && (
                  <span className="text-xs text-slate-500">
                    UOM: {item.item_snapshot.UOM}
                  </span>
                )}
                {item.item_snapshot?.HSN && (
                  <span className="text-xs text-slate-500">
                    HSN: {item.item_snapshot.HSN}
                  </span>
                )}
              </div>

            </div>

          </div>

          {/* DELETE */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={() => onRemove(index)}
            data-testid={`remove-item-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>

        </div>

        {/* QTY / RATE / DISCOUNT */}
        <div className="grid grid-cols-3 gap-3 mb-3">

          <div>
            <label className="text-xs text-slate-500 mb-1 block">Qty</label>
            <Input
              type="number"
              min="1"
              step="1"
              value={item.qty || ''}
              onChange={(e) => onChange(index, 'qty', e.target.value)}
              className="h-10 text-sm"
            />
          </div>

          <div>
            <label className="text-xs text-slate-500 mb-1 block">Rate (₹)</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={item.rate || ''}
              onChange={(e) => onChange(index, 'rate', e.target.value)}
              className="h-10 text-sm"
            />
          </div>

          <div>
            <label className="text-xs text-slate-500 mb-1 block">Disc %</label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={item.discount_percent || ''}
              onChange={(e) => onChange(index, 'discount_percent', e.target.value)}
              className="h-10 text-sm"
            />
          </div>

        </div>

        {/* TOTALS */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-200">

          <div className="text-xs text-slate-500">
            <p>Taxable: {formatCurrency(item.taxable_amount)}</p>
            <p>
              Tax: {formatCurrency(
                (item.tax_breakup?.CGST || 0) +
                (item.tax_breakup?.SGST || 0) +
                (item.tax_breakup?.IGST || 0)
              )}
              {item.tax_breakup?.IGST > 0 ? ' (IGST)' : ' (CGST+SGST)'}
            </p>
          </div>

          <div className="text-right">
            <p className="text-xs text-slate-500">Total</p>
            <p className="text-lg font-semibold text-slate-900">
              {formatCurrency(item.total)}
            </p>
          </div>

        </div>

      </CardContent>
    </Card>
  );
};

export default LineItemRow;